import { Container, IDestroyOptions, Point } from "pixi.js";
import { BasePageView } from "../../../../common/components/BasePageView";
import { RaidResourcePackage } from "../../resources/RaidResourcePackage";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { Card } from "../../components/cards/Card";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";
import { IRaidController } from "../../controllers/IRaidController";
import { NoCacheDynamicPackage } from "../../../../common/resources/NoCacheDynamicPackage";
import { RaidAction } from "../../../../game/flow/impl/diceActions/raidAction/RaidAction";

export class CardsPage extends BasePageView {
    private static readonly CARDS_CONTAINER_SCALE = 1;
    private static readonly RADIANS_DEFAULT_VALUE = 6.28319;
    private static readonly SWITCH_CARDS_ANIMATION_TIME = 0.3;

    private readonly raidResources: RaidResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly raidController: IRaidController;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly raidDynamicResources: NoCacheDynamicPackage;
    private readonly action: RaidAction;
    private fadeScreen: FadeScreen;
    private cardsContainer: Container;
    private cards: Array<Card>;
    private cardsMoveStartPosition: Point;
    private viewCompletionSource: PromiseCompletionSource<void>;
    private isFirstShow: boolean;
    private currentAnimation: gsap.core.Timeline;

    public constructor(
        raidResources: RaidResourcePackage,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        raidController: IRaidController,
        userProfileResources: UserProfileResourcePackage,
        canvasScaler: ICanvasScaler,
        isFirstShow: boolean,
        raidDynamicResources: NoCacheDynamicPackage,
        action: RaidAction,
        viewCompletionSource: PromiseCompletionSource<void>
    ) {
        super(canvasScaler);
        this.raidResources = raidResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.raidController = raidController;
        this.userProfileResources = userProfileResources;
        this.isFirstShow = isFirstShow;
        this.raidDynamicResources = raidDynamicResources;
        this.action = action;
        this.viewCompletionSource = viewCompletionSource;
        this.cards = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.showComponents.bind(this));
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.7, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.cardsContainer = new Container();
        this.cardsContainer.scale.set(CardsPage.CARDS_CONTAINER_SCALE);
        this.addChild(this.cardsContainer);
        this.cardsContainer.addChild(this.cardContainerFactory());
        super.build();
    }

    private cardContainerFactory(): Card {
        const card = new Card(this.commonResources, this.raidResources, this.userProfileResources, this.canvasScaler);
        this.cards.push(card);
        return card;
    }

    private async showComponents(): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.fadeScreen.fadeIn();
        this.cards.forEach(card => {
            animationTimeLine.fromTo(card, { x: this.cardsMoveStartPosition.x, y: this.cardsMoveStartPosition.y, angle: card.angle - 90 }, {
                x: card.x,
                y: card.y,
                angle: card.angle,
                duration: 0.5,
                ease: "back.out(1)",
                onStart: () => this.soundResources.assets_sounds_Popups_Entering_mp3.play()
            }, "<25%");
        });
        await animationTimeLine.play();
        animationTimeLine.kill();
        await this.selectCard(this.cards[0]);
    }

    private async selectCard(card: Card): Promise<void> {
        await this.startSwitchAndUpdateCards(card);
        if (this.isFirstShow) {
            const cardTutorialAnimation = this.currentAnimation = card.getTutorialAnimations();
            await cardTutorialAnimation.play();
        } else {
            card.fadeIn();
        }
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.to(this.cardsContainer.scale, {
            x: this.cardsContainer.scale.x * 1.7,
            y: this.cardsContainer.scale.y * 1.7,
            duration: 0.4,
            ease: "power2.out",
            onStart: () => this.soundResources.assets_sounds_Popups_Entering_mp3.play()
        });
        await animationTimeLine.play();
        animationTimeLine.kill();
        this.viewCompletionSource.complete();
    }

    private async startSwitchAndUpdateCards(card: Card): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        this.soundResources.assets_sounds_Card_Flip_mp3.play();

        //start fly and rotate card
        animationTimeLine.to(card.scale, { x: CardsPage.CARDS_CONTAINER_SCALE * 0.5, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power.in" }, "<");
        animationTimeLine.to(card.skew, { y: CardsPage.RADIANS_DEFAULT_VALUE * 0.25, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "none" }, "<");
        animationTimeLine.to(card, { y: card.y - card.height * 1.1, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" }, "<");
        animationTimeLine.to(card, { rotation: -CardsPage.RADIANS_DEFAULT_VALUE * 0.5, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.in" }, "<");

        //update card
        animationTimeLine.add(() => {
            this.cardsContainer.setChildIndex(card, this.cards.length - 1);
            this.updateCard(card);
        });

        //finish fly and rotate card
        animationTimeLine.set(card.skew, { y: CardsPage.RADIANS_DEFAULT_VALUE * 0.75 });
        animationTimeLine.to(card.scale, { x: CardsPage.CARDS_CONTAINER_SCALE, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" });
        animationTimeLine.to(card.skew, { y: CardsPage.RADIANS_DEFAULT_VALUE, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" }, "<");
        animationTimeLine.to(card, { y: card.y, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.in" }, "<");
        animationTimeLine.to(card, { rotation: -CardsPage.RADIANS_DEFAULT_VALUE, duration: CardsPage.SWITCH_CARDS_ANIMATION_TIME, ease: "power1.out" }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private updateCard(willBeChosenCard: Card): void {
        willBeChosenCard.updateAndShowCardContent(this.action.diceRollRaidCard, this.raidDynamicResources);
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
    }

    protected override layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.cardsContainer.position.set(width * 0.5, height * 0.5);
        this.layoutCards(width, height);
        this.cardsMoveStartPosition = new Point(0, height);
        super.layout(width, height);
    }

    private layoutCards(width: number, height: number): void {
        const cardAngle = 15;
        let cardIndex = 0;
        this.cards.forEach(card => {
            card.angle = cardAngle * cardIndex;
            card.position.x = card.width * 0.25 * cardIndex;
            card.position.y = cardIndex === 0 ? 0 : card.height * 0.05;
            cardIndex--;
            card.layout(width, height);
        })
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        super.destroy(options);
    }
}
